import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Download, SquareMousePointer, Star, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import OverlayShell from "../components/OverlayShell";
import MediaTile, { TileMarks, VideoBadge } from "../components/MediaTile";
import QueryState from "../components/QueryState";
import { useMediaDownload } from "../lib/media/download";
import { fetchExportItems } from "../lib/media/export";
import { fetchFavoritesPage, removeFavorites, type FavoritesCursor } from "../lib/media/favorites";
import { countTimeline } from "../lib/media/timeline";
import { useSignedUrls } from "../lib/media/useSignedUrls";
import { queryKeys } from "../lib/queryKeys";
import { useLoadMoreSentinel } from "../lib/useLoadMoreSentinel";
import { useMediaTasks } from "../lib/mediaTasks";
import { useConfirm } from "../lib/confirm";
import { useSelection } from "../lib/useSelection";
import { useToast } from "../lib/toast";
import MediaDetail from "./MediaDetail";

export default function Favorites({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const download = useMediaDownload();
  const { exporting } = useMediaTasks();
  const [selectMode, setSelectMode] = useState(false);
  const { selected, toggle, selectAll, clear } = useSelection();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 並びは「お気に入りに追加した順」。撮影日時順のタイムラインとは分けている。
  // キーは favorites の接頭辞に載せ、登録・解除の invalidate でそのまま取り直される
  const favorites = useInfiniteQuery({
    queryKey: queryKeys.favoritesList,
    queryFn: ({ pageParam }) => fetchFavoritesPage(pageParam),
    initialPageParam: null as FavoritesCursor | null,
    getNextPageParam: (page) => page.next,
  });

  // 詳細表示の「n / 全体」用
  const total = useQuery({
    queryKey: queryKeys.favoritesCount,
    queryFn: () => countTimeline({ kind: "favorite" }),
  });

  const items = useMemo(
    () => favorites.data?.pages.flatMap((page) => page.items) ?? [],
    [favorites.data],
  );

  const thumbKeys = useMemo(() => items.map((item) => item.thumbnail_key), [items]);
  const { urls: thumbUrls, refresh: refreshThumbs } = useSignedUrls(thumbKeys);

  const sentinel = useLoadMoreSentinel(favorites);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = favorites;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /** 選んだぶんを端末に保存する。流れはタイムラインの選択モードと同じ */
  async function downloadSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    // 取りやめたときは選択を残す。選び直しからやり直させない
    if (await download(() => fetchExportItems(ids))) {
      setSelectMode(false);
      clear();
    }
  }

  async function removeSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      message: `${String(ids.length)}件のお気に入りを解除します。`,
      confirmLabel: "解除",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await removeFavorites(ids);
      setSelectMode(false);
      clear();
      await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
      await queryClient.invalidateQueries({ queryKey: queryKeys.favorites });
      toast(`${String(ids.length)}件のお気に入りを解除しました。`);
    } catch (e) {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("お気に入りの解除に失敗", e);
      toast(`${String(ids.length)}件のお気に入りの解除に失敗しました。`, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OverlayShell title="お気に入り" icon={Star} onClose={onClose} width="grid">
      {/* 選択中は下部バーが被さるぶんの逃げ幅を足す(PC はバーがカード内に収まる) */}
      <div className={`flex w-full flex-col gap-3 p-4 ${selectMode ? "pb-24 xl:pb-4" : ""}`}>
        {items.length > 0 && (
          // PC ではグリッドを送っても操作は手元に残す。見出しは xl では
          // スクロールの外(静的)なので、上に貼り付けても重ならない。
          // 余白ごと帯にして、下のタイルが脇から覗かないようにする
          <div className="hidden justify-end xl:sticky xl:top-0 xl:z-10 xl:-mx-4 xl:-mt-4 xl:flex xl:bg-[var(--color-bg)] xl:px-4 xl:pt-4 xl:pb-3">
            <button
              type="button"
              onClick={() => {
                setSelectMode((current) => !current);
                clear();
              }}
              aria-pressed={selectMode}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition ${
                selectMode
                  ? "bg-[var(--color-sub)] font-medium text-[var(--color-bg)] hover:opacity-85"
                  : "border border-[var(--color-line)] bg-white text-[var(--color-ink)] hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
              }`}
            >
              <SquareMousePointer className="size-4" aria-hidden />
              {selectMode ? "選択をやめる" : "選択"}
            </button>
          </div>
        )}
        <QueryState
          isPending={favorites.isPending}
          isError={favorites.isError}
          errorMessage="お気に入りの取得に失敗しました。"
          isEmpty={favorites.isSuccess && items.length === 0}
          emptyMessage="まだお気に入りはありません。"
          emptyClassName="p-10 text-center text-sm"
        />

        <div className="grid grid-cols-3 gap-1.5 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
          {items.map((item) => (
            <MediaTile
              key={item.id}
              thumbUrl={thumbUrls[item.thumbnail_key]}
              onThumbError={refreshThumbs}
              showCheck={selectMode}
              isSelected={selected.has(item.id)}
              onClick={() => {
                if (selectMode) toggle(item.id);
                else setDetailId(item.id);
              }}
            >
              {item.type === "video" && <VideoBadge durationSeconds={item.duration_seconds} />}
              <TileMarks mediaId={item.id} />
            </MediaTile>
          ))}
        </div>

        <div ref={sentinel} className="h-8" aria-hidden />
        {isFetchingNextPage && (
          <p className="text-center text-xs text-[var(--color-sub)]">読み込み中...</p>
        )}
      </div>

      {items.length > 0 && (
        <div
          className={`pointer-events-none fixed inset-x-0 z-10 flex justify-end px-4 xl:hidden ${
            selectMode
              ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
              : "bottom-[calc(1rem+env(safe-area-inset-bottom))]"
          }`}
        >
          <button
            type="button"
            onClick={() => {
              setSelectMode((current) => !current);
              clear();
            }}
            aria-pressed={selectMode}
            aria-label={selectMode ? "選択をやめる" : "選択"}
            className={`pointer-events-auto flex size-14 items-center justify-center rounded-full shadow-lg ${
              selectMode
                ? "bg-[var(--color-sub)] text-[var(--color-bg)]"
                : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
            }`}
          >
            {selectMode ? (
              <X className="size-6" aria-hidden />
            ) : (
              <SquareMousePointer className="size-6" aria-hidden />
            )}
          </button>
        </div>
      )}

      {selectMode && (
        // 項目は固定(件数・すべて選択・お気に入り解除)。やめる操作は選択ボタン側が持つ。
        // 全画面のスマホ・タブレットはスクロールに関わらず画面下部に留める(fixed)。
        // モーダルの PC はカードの中に収める(sticky)
        <div className="fixed inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3 border-t border-[var(--color-line)] bg-white p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] xl:sticky xl:mt-auto">
          <span className="text-xs text-[var(--color-sub)] tabular-nums">{selected.size}件</span>
          <button
            type="button"
            onClick={() => {
              // 全件選んだ状態でもう一度押すと、選び直しやすいよう全解除に切り替わる
              if (selected.size === items.length) clear();
              else selectAll(items.map((item) => item.id));
            }}
            className="flex items-center gap-1 rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <CheckCheck className="size-4" aria-hidden />
            {selected.size === items.length ? "選択を解除" : "すべて選択"}
          </button>
          <button
            type="button"
            onClick={() => void downloadSelected()}
            disabled={selected.size === 0 || exporting !== null}
            aria-label="ダウンロード"
            className="flex shrink-0 items-center gap-1 rounded-lg border border-[var(--color-line)] px-3 py-2 text-sm transition-colors disabled:opacity-50 enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)]"
          >
            <Download className="size-4" aria-hidden />
            <span className="hidden xl:inline">ダウンロード</span>
          </button>
          <button
            type="button"
            onClick={() => void removeSelected()}
            disabled={busy || selected.size === 0}
            aria-label="お気に入り解除"
            className="flex shrink-0 items-center gap-1 rounded-lg bg-[var(--color-plum)] px-3 py-2 text-sm text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            <Star className="size-4" aria-hidden />
            <span className="hidden xl:inline">お気に入り解除</span>
          </button>
        </div>
      )}

      {detailId !== null && (
        <MediaDetail
          items={items}
          currentId={detailId}
          onChange={setDetailId}
          onClose={() => {
            setDetailId(null);
          }}
          thumbnailUrls={thumbUrls}
          onReachEnd={loadMore}
          total={total.data}
        />
      )}
    </OverlayShell>
  );
}
